import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Phone,
  Truck,
  Apple,
  Package,
  CheckCircle2,
  Star,
  Clock,
  Headphones,
  Shield,
  Snowflake,
  MapPin,
  Mail,
  DollarSign,
  Users,
  Wrench,
  Facebook,
} from "lucide-react";
import { SectionHeader } from "@/components/SectionHeader";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { heroImage, heroPreload, truckImage } from "@/lib/images";
import { SITE } from "@/lib/site";
import { mergeHead, pageHead } from "@/lib/seo";
import { jsonLdScript, localBusinessGraph } from "@/lib/schema";
import { StatsBar } from "@/components/StatsBar";

export const Route = createFileRoute("/")({
  head: () =>
    mergeHead(
      pageHead({
        title: "Fox Trucking LLC — Coast-to-Coast Freight Since 2012",
        description: `New Jersey trucking company hauling produce, dry goods and general freight nationwide. On-time delivery, elite communication, driver-first culture. Call ${SITE.phoneDisplay}.`,
        path: "/",
        ogTitle: "Fox Trucking LLC — Coast-to-Coast Freight",
        ogDescription:
          "Reliable long-haul freight since 2012. Produce, dry goods, general freight.",
      }),
      {
        links: [
          {
            rel: "preload",
            as: "image",
            href: heroPreload.href,
            type: heroPreload.type,
            imageSrcSet: heroPreload.imageSrcSet,
            imageSizes: heroPreload.imageSizes,
            fetchPriority: "high",
          },
        ],
        scripts: [jsonLdScript(localBusinessGraph())],
      },
    ),
  component: Home,
});

function parseInquiry(raw: Record<string, FormDataEntryValue>) {
  const name = String(raw.name ?? "").trim();
  const company = String(raw.company ?? "").trim();
  const email = String(raw.email ?? "").trim();
  const phone = String(raw.phone ?? "").trim();
  const message = String(raw.message ?? "").trim();
  if (!name || name.length > 120) return { error: "Name required" };
  if (!email || email.length > 255 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { error: "Valid email required" };
  }
  if (company.length > 120 || phone.length > 30) return { error: "Tell us about your load" };
  if (message.length < 5 || message.length > 1500) return { error: "Tell us about your load" };
  return { data: { name, company, email, phone, message } };
}

function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-secondary text-secondary-foreground">
        <ResponsiveImage
          {...heroImage}
          alt="Freight truck on American highway at sunset"
          pictureClassName="absolute inset-0 h-full w-full"
          className="h-full w-full object-cover opacity-40"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-transparent" />
        <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-24 md:py-32 lg:grid-cols-2 lg:px-8 lg:py-40">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-secondary/50 px-3 py-1 font-display text-xs tracking-[0.3em] text-accent">
              <span className="h-2 w-2 rounded-full bg-accent" /> Established 2012 · New Jersey
            </div>
            <h1 className="mt-6 min-h-[5.4rem] font-display text-5xl leading-[0.9] text-secondary-foreground sm:min-h-[6.75rem] sm:text-6xl lg:min-h-[8.1rem] lg:text-7xl xl:min-h-[10.8rem] xl:text-8xl">
              Freight that <span className="text-accent">moves</span>
              <br />
              <span className="text-stroke-gold">coast to coast.</span>
            </h1>
            <p className="mt-6 min-h-[6.75rem] max-w-xl text-lg leading-7 text-secondary-foreground/80 sm:min-h-[4.5rem]">
              Produce, dry goods, and general freight — hauled by drivers who care, dispatched by
              people who answer the phone. On time. Every time.
            </p>
            <div className="mt-8 flex min-h-48 flex-col gap-3 sm:min-h-14 sm:flex-row sm:flex-nowrap">
              <Link
                to="/contact"
                className="group inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-md bg-primary px-6 font-display tracking-wider text-primary-foreground hover:bg-primary/90"
              >
                Book a Load{" "}
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </Link>
              <Link
                to="/drive-with-us"
                className="inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-md bg-accent px-6 font-display tracking-wider text-accent-foreground hover:bg-accent/90"
              >
                Drive With Us
              </Link>
              <a
                href={`tel:${SITE.phoneTel}`}
                className="inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-md border border-secondary-foreground/30 px-6 font-display tracking-wider text-secondary-foreground hover:border-accent hover:text-accent"
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
              </a>
            </div>
            <StatsBar variant="hero" />
          </div>
        </div>
        <div className="diag-stripes absolute bottom-0 left-0 right-0 h-3" />
      </section>

      {/* SERVICES */}
      <section id="services" className="scroll-mt-20 bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="What We Haul"
            title="Built for the loads that matter."
            subtitle="From fresh produce that can't wait to dry goods filling shelves nationwide — we move it with care."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                i: Apple,
                t: "Fresh Produce",
                d: "Time-sensitive perishables with strict temp control and tight scheduling.",
                to: "/refrigerated-freight" as const,
              },
              {
                i: Package,
                t: "Dry Goods",
                d: "Packaged consumer goods, paper products, building materials — clean and on time.",
                to: "/dry-van-freight" as const,
              },
              {
                i: Truck,
                t: "General Freight",
                d: "Full truckload (FTL) dry van service across the lower 48.",
                to: "/dry-van-freight" as const,
              },
              {
                i: Snowflake,
                t: "Temp Controlled",
                d: "Reefer service for sensitive freight — set the temp, we'll hold it.",
                to: "/refrigerated-freight" as const,
              },
            ].map((s) => (
              <Link
                key={s.t}
                to={s.to}
                className="group relative overflow-hidden rounded-lg border border-border bg-card p-8 transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <s.i className="h-10 w-10 text-primary" aria-hidden="true" />
                <h3 className="mt-6 font-display text-2xl text-card-foreground">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
                <span className="mt-4 inline-flex font-display text-xs tracking-wider text-primary opacity-0 transition group-hover:opacity-100">
                  Learn more →
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-sm text-muted-foreground">
            We run long-haul and regional lanes from New Jersey — see{" "}
            <Link
              to="/coverage"
              className="font-display tracking-wider text-primary underline-offset-2 hover:underline"
            >
              where we haul
            </Link>
            .
          </p>
        </div>
      </section>

      {/* ABOUT / WHY */}
      <section
        id="about"
        className="scroll-mt-20 relative bg-secondary py-24 text-secondary-foreground"
      >
        <div className="absolute inset-0 bg-noise opacity-60" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-2 lg:px-8">
          <div>
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg border-4 border-accent">
              <ResponsiveImage
                {...truckImage}
                alt="Fox Trucking blue Volvo with reefer trailer"
                pictureClassName="absolute inset-0 h-full w-full"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div>
            <SectionHeader
              tone="dark"
              eyebrow="Why Fox Trucking"
              title="Pricing fluctuates. Our reliability doesn't."
              subtitle={`Founded in New Jersey in ${SITE.foundingYear} by ${SITE.founderName}, Fox Trucking has spent over a decade building a name on one thing: doing what we say we'll do.`}
            />
            <p className="mt-4 text-sm text-secondary-foreground/70">
              <Link
                to="/about"
                className="font-display tracking-wider text-accent underline-offset-2 hover:underline"
              >
                More about Fox Trucking
              </Link>
              {" · "}
              <Link
                to="/coverage"
                className="font-display tracking-wider text-accent underline-offset-2 hover:underline"
              >
                Coast-to-coast coverage
              </Link>
            </p>
            <ul className="mt-8 space-y-4">
              {[
                {
                  i: Clock,
                  t: "On-Time Every Time",
                  d: "Tight scheduling, proactive routing, no surprises.",
                },
                {
                  i: Headphones,
                  t: "Elite Communication",
                  d: "Real people answer the phone. Updates before you ask.",
                },
                {
                  i: Shield,
                  t: "Customers First",
                  d: "We treat your freight like it's our own livelihood — because it is.",
                },
              ].map((b) => (
                <li key={b.t} className="flex gap-4">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-accent text-accent-foreground">
                    <b.i className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-display text-lg text-accent">{b.t}</div>
                    <div className="text-sm text-secondary-foreground/70">{b.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* DRIVERS */}
      <section
        id="drivers"
        className="scroll-mt-20 relative overflow-hidden bg-primary py-24 text-primary-foreground"
      >
        <div className="diag-stripes absolute left-0 top-0 h-2 w-full" />
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-[2fr_1fr]">
            <div>
              <div className="font-display text-xs tracking-[0.3em] text-primary-foreground">
                CLASS A CDL DRIVERS
              </div>
              <h2 className="mt-3 font-display text-5xl leading-[0.95] md:text-6xl lg:text-7xl">
                We're hiring drivers
                <br />
                who actually get home.
              </h2>
              <p className="mt-5 max-w-2xl text-lg text-primary-foreground">
                Competitive pay. Newer equipment. Dispatchers who treat you like a person. Apply
                today — we respond in 24–48 hours.
              </p>
            </div>
            <Link
              to="/drive-with-us"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-accent px-8 py-5 font-display text-lg tracking-wider text-accent-foreground hover:bg-accent/90"
            >
              Apply Now{" "}
              <ArrowRight
                className="h-5 w-5 transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-4">
            {[
              {
                i: DollarSign,
                t: "Competitive Pay",
                d: "Top-of-market rates for experienced drivers.",
              },
              {
                i: Truck,
                t: "Modern Equipment",
                d: "Late-model tractors. Clean, maintained, ready.",
              },
              { i: Users, t: "Real Dispatch", d: "Humans on the phone, 24/7. No bots." },
              {
                i: Wrench,
                t: "Driver Support",
                d: "Maintenance, fuel cards, and roadside backup.",
              },
            ].map((p) => (
              <div
                key={p.t}
                className="rounded-lg border border-primary-foreground/20 bg-primary-foreground/5 p-6"
              >
                <p.i className="h-8 w-8 text-accent" aria-hidden="true" />
                <div className="mt-3 font-display text-lg">{p.t}</div>
                <div className="mt-1 text-sm text-primary-foreground">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Reviews"
            title="Trusted by shippers across the country."
            align="center"
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              {
                q: "Fox Trucking always delivers when they say they will. Communication is the best I've worked with.",
                n: "Shipping Manager",
              },
              {
                q: "Reliable, professional, and they actually pick up the phone. Hard to find these days.",
                n: "Logistics Coordinator",
              },
              {
                q: "Been working with Umut and the team for years. They treat my freight like it's their own.",
                n: "Produce Broker",
              },
            ].map((t, i) => (
              <div key={i} className="rounded-lg border border-border bg-card p-8">
                <div className="flex gap-1 text-accent">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-current" aria-hidden="true" />
                  ))}
                </div>
                <p className="mt-4 text-card-foreground">"{t.q}"</p>
                <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" /> {t.n} ·
                  Google Review
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <ContactSection />
    </>
  );
}

function ContactSection() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const parsed = parseInquiry(Object.fromEntries(new FormData(e.currentTarget)));
    if ("error" in parsed) {
      setStatus(parsed.error ?? "Tell us about your load");
      return;
    }
    setLoading(true);
    const d = parsed.data;
    const body = `${d.message}%0D%0A%0D%0A--%0D%0A${d.name}${d.company ? " · " + d.company : ""}%0D%0A${d.email}${d.phone ? " · " + d.phone : ""}`;
    window.location.href = `mailto:${SITE.email}?subject=Freight Inquiry — ${d.name}&body=${body}`;
    setStatus(
      `Your email app is opening with this message ready — hit send, or call ${SITE.phoneDisplay}.`,
    );
    setTimeout(() => setLoading(false), 600);
  }

  return (
    <section id="contact" className="scroll-mt-20 bg-muted py-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeader
          eyebrow="Contact"
          title="Let's move your freight."
          subtitle="Call, email, or fill in your load details below — real quotes from real people."
        />
        <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_1.3fr]">
          <div className="space-y-4">
            <ContactCard
              icon={Phone}
              label="Call Dispatch"
              value={SITE.phoneDisplay}
              href={`tel:${SITE.phoneTel}`}
            />
            <ContactCard
              icon={Mail}
              label="Email"
              value={SITE.email}
              href={`mailto:${SITE.email}`}
            />
            <ContactCard icon={MapPin} label="Based In" value="New Jersey · Serving the lower 48" />
            <ContactCard icon={Clock} label="Hours" value="24/7 Dispatch · Mon–Sun" />
            <ContactCard
              icon={Facebook}
              label="Follow"
              value={SITE.facebookHandle}
              href={SITE.facebookUrl}
              external
            />
          </div>

          <form onSubmit={submit} className="rounded-lg border border-border bg-card p-8 shadow-sm">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Your Name" name="name" required />
              <Field label="Company" name="company" />
              <Field label="Email" name="email" type="email" required />
              <Field label="Phone" name="phone" type="tel" />
            </div>
            <div className="mt-4">
              <label
                htmlFor="contact-message"
                className="font-display text-xs tracking-widest text-muted-foreground"
              >
                LOAD DETAILS <span className="text-primary">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={6}
                required
                maxLength={1500}
                placeholder="Pickup city/state, delivery city/state, commodity, weight, pickup date..."
                autoComplete="off"
                className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <button
              disabled={loading}
              type="submit"
              className="mt-6 w-full rounded-md bg-primary px-6 py-4 font-display tracking-widest text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {loading ? "Opening email..." : "Send Inquiry by Email"}
            </button>
            {status && (
              <p className="mt-3 text-center text-sm text-foreground" role="status">
                {status}
              </p>
            )}
            <p className="mt-3 text-center text-xs text-muted-foreground">
              This opens your email app with the details filled in. Prefer to talk? Call{" "}
              {SITE.phoneDisplay}.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function ContactCard({
  icon: Icon,
  label,
  value,
  href,
  external,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
  external?: boolean;
}) {
  const inner = (
    <div className="flex items-start gap-4 rounded-lg border border-border bg-card p-6 transition hover:border-primary">
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div>
        <div className="font-display text-xs tracking-widest text-muted-foreground">
          {label.toUpperCase()}
        </div>
        <div className="mt-1 font-display text-lg text-card-foreground">{value}</div>
      </div>
    </div>
  );
  if (href)
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {inner}
      </a>
    );
  return inner;
}

const fieldAutocomplete: Record<string, string> = {
  name: "name",
  company: "organization",
  email: "email",
  phone: "tel",
};

function Field({
  label,
  name,
  type = "text",
  required,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
}) {
  const id = `contact-${name}`;
  return (
    <div>
      <label htmlFor={id} className="font-display text-xs tracking-widest text-muted-foreground">
        {label.toUpperCase()}
        {required && <span className="text-primary"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        name={name}
        required={required}
        maxLength={255}
        autoComplete={fieldAutocomplete[name] ?? "off"}
        className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  );
}
